import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  User,
  signInWithPopup,
  signInWithRedirect,
  browserPopupRedirectResolver,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  MultiFactorResolver,
  getMultiFactorResolver,
  getAdditionalUserInfo,
  GoogleAuthProvider,
  signInWithCredential,
} from 'firebase/auth';
import { markRedirectPending } from '../utils/authRedirectPending';
import { doc, onSnapshot } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { auth, googleProvider, twitterProvider, db } from '../firebase';
import { UserAccount } from '../types/user';
import { UserService } from '../services/userService';
import { useAnalytics } from '../hooks/useAnalytics';
import analyticsService from '../services/analytics';
import { getFirstTouch, isoWeek, getAnonymousId } from '../utils/acquisitionTracking';
import { MicrosoftAuthStandard } from '../services/microsoftAuthStandard';
import { currentSignInEnvironment, googleSignInMode, shouldFallBackToRedirect } from '../utils/googleSignInMode';
import UsageTrackingService from '../services/usageTrackingService';
import { usePendingSaveStore } from '../stores/pendingSaveStore';
import { useSavedPortfolioStore } from '../stores/savedPortfolioStore';
import { useSubscriptionStore } from '../stores/subscriptionStore';
import { refreshEntitlements } from '../services/entitlementService';
import { clearServerProfile, fetchServerProfile, getServerProfile } from '../services/profileService';
import { ACCOUNT_EXISTS_MESSAGE } from '../auth/signInMessages';

export type GoogleSignInOutcome = 'signed-in' | 'redirecting' | 'incomplete';


interface AuthContextType {
  user: User | null;
  userAccount: UserAccount | null;
  loading: boolean;
  error: string | null;
  mfaResolver: MultiFactorResolver | null;
  mfaPending: boolean;
  /** `'signed-in'` is the only outcome a caller may count as a sign-in:
   *  `'redirecting'` means the page is about to navigate away (the boot handler
   *  records the result when it comes back), `'incomplete'` means a second
   *  factor is pending or an account conflict was reported (ROADMAP 1.37). */
  signInWithGoogle: () => Promise<GoogleSignInOutcome>;
  /** Sign in with a Google ID token from One Tap (ROADMAP 1.37 item 3) —
   *  the same account, analytics and second-factor handling as the button. */
  signInWithGoogleCredential: (idToken: string) => Promise<GoogleSignInOutcome>;
  signInWithMicrosoft: () => Promise<void>;
  signInWithTwitter: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUserAccount: () => Promise<void>;
  clearMfaState: () => void;
  onMfaSuccess: () => void;
}

/** A redirect sign-in's provider → the method analytics records, the same
 *  names the popup paths use ([[BUGS #527]]). */
export const REDIRECT_METHOD: Record<string, string> = {
  'google.com': 'google',
  'microsoft.com': 'microsoft',
  'twitter.com': 'twitter',
};

/**
 * What a sign-in returning from a redirect records — the success the dialog
 * could not record when the redirect started, and a sign-up for a new account.
 * Every provider that returns this way, not only Google: Microsoft signs in by
 * redirect, and its sign-ups never reached analytics ([[BUGS #527]]).
 */
export function recordRedirectSignIn(
  info: { providerId?: string | null; isNewUser?: boolean } | null | undefined,
  events: { login: (method: string) => void; signUp: (method: string) => void },
  service: { trackSignIn: (status: 'success', method: string, source: string) => void;
    trackSignUp: (method: string, source: string) => void },
): void {
  const method = REDIRECT_METHOD[info?.providerId || ''];
  if (!info || !method) return;
  service.trackSignIn('success', method, 'redirect');
  events.login(method);
  if (info.isNewUser) {
    events.signUp(method);
    // No email: a redirect result is logged nowhere with it (invariant 10).
    service.trackSignUp(method, 'login_dialog');
  }
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** The auth state where there is one, else undefined — for public surfaces
 *  (the blog, the landing page) that render with or without a provider and
 *  only need to know whether to show a signed-out ask (ROADMAP 0.R R9). */
export const useOptionalAuth = (): AuthContextType | undefined => useContext(AuthContext);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [userAccount, setUserAccount] = useState<UserAccount | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
  const [mfaPending, setMfaPending] = useState(false);
  const { trackEvents } = useAnalytics();

  // Guards async operations from stale auth state (rapid login/logout)
  const authSequenceRef = useRef(0);

  // Suppress unused navigate warning - kept for future use
  void navigate;

  // Clear MFA state (called when user cancels MFA)
  const clearMfaState = useCallback(() => {
    setMfaResolver(null);
    setMfaPending(false);
    setLoading(false);
    setError(null);
  }, []);

  // Called when MFA verification succeeds
  const onMfaSuccess = useCallback(() => {
    setMfaResolver(null);
    setMfaPending(false);
    setError(null);
    // User will be set by onAuthStateChanged
  }, []);

  // Function to load user account data
  const loadUserAccount = useCallback(async (firebaseUser: User) => {
    try {
      setError(null);
      const account = await UserService.getOrCreateUser(firebaseUser);
      setUserAccount(account);
    } catch (err: any) {
      setError(err instanceof Error ? err.message : (typeof err === 'string' ? err : 'Authentication error'));
      setUserAccount(null);
      clearServerProfile();   // never carry one user's preferences into another's session
    }
  }, []);

  // Function to refresh user account
  const refreshUserAccount = useCallback(async () => {
    if (user) {
      await loadUserAccount(user);
    }
  }, [user, loadUserAccount]);

  // Note: Microsoft OAuth callbacks are now handled by the OAuthCallback component/route
  // This prevents double processing and ensures proper routing

  // The boot effect below runs ONCE (it owns the auth subscription); it reads
  // analytics through a ref so a new `trackEvents` identity never re-runs it.
  const trackEventsRef = useRef(trackEvents);
  trackEventsRef.current = trackEvents;

  useEffect(() => {
    let unsubscribeSnapshot: (() => void) | undefined;

    // A redirect sign-in (Microsoft, or Google on a phone — ROADMAP 1.37)
    // returns here, on a cold boot. The session itself arrives through
    // onAuthStateChanged below; this reads the RESULT once, for what only the
    // result carries: sign-up analytics, the MFA step, and the errors — which
    // the old `checkAndHandleRedirect` swallowed, so a redirect that needed a
    // second factor simply never finished.
    MicrosoftAuthStandard.handleRedirectResult()
      .then((result) => {
        if (!result) return;
        recordRedirectSignIn(getAdditionalUserInfo(result), trackEventsRef.current, analyticsService);
      })
      .catch((error: any) => {
        if (error?.code === 'auth/multi-factor-auth-required') {
          setMfaResolver(getMultiFactorResolver(auth, error));
          setMfaPending(true);
          return;
        }
        console.warn('[auth] redirect sign-in did not complete:', error?.code || 'unknown');
        setError('Sign-in did not complete. Please try again.');
      });

    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      // Increment sequence to invalidate any in-flight async from previous auth state
      const currentSequence = ++authSequenceRef.current;
      setUser(firebaseUser);

      // Check Firestore connection first
      // NOTE — there used to be a synthetic connectivity probe here, and a
      // `return` when it failed ([[BUGS #151]]). That made one transient read
      // failure disable the ENTIRE entitlement pipeline for the session: the
      // snapshot listener below was never attached, so `setEntitlements` never
      // ran, `subscriptionStore.plan` stayed at its initial `free`, and a
      // paying subscriber was shown the free tier on /plans and a paywall on
      // their own risk report — until a reload that happened to succeed.
      //
      // It was fragile in a way worth remembering: the probe read a document
      // the rules deny, so it treated `permission-denied` as PROOF of a working
      // connection. Anything else — a QUIC/`Listen` reset of the kind staging
      // is known for ([[BUGS #32]]), an ad-blocker, a moment offline — read as
      // "no database", with no retry.
      //
      // The listener does not need the probe's permission. Firestore's SDK
      // already retries, resumes and reports through `onSnapshot`'s error
      // callback, which is where connection trouble is now surfaced.
      
      // Clean up previous snapshot listener
      if (unsubscribeSnapshot) {
        unsubscribeSnapshot();
        unsubscribeSnapshot = undefined;
      }
      
      if (firebaseUser) {
        // Load user account
        await loadUserAccount(firebaseUser);

        // Bail if auth state changed while we were loading
        if (authSequenceRef.current !== currentSequence) return;

        // What they may do, from the server that enforces it ([[BUGS #153]]).
        // Deliberately not awaited: entitlements gate paid surfaces, not the
        // app, and a slow answer must not hold up sign-in. An unresolved
        // result leaves the store untouched, so surfaces that could offer to
        // charge stay silent rather than guess ([[BUGS #147]]).
        refreshEntitlements()
          .then(() => analyticsService.setUserProperties({
            subscription_tier: useSubscriptionStore.getState().plan,
          }))
          .catch(() => { /* refreshEntitlements never rejects; belt and braces */ });

        // The profile fields the gateway owns, from the gateway ([[BUGS #190]]).
        // Not awaited either, and for the same reason — but note the failure
        // shape is deliberately different from entitlements': `null` means "we
        // could not find out", and we then leave whatever the snapshot gave us
        // rather than clearing anything. Reverting someone's theme because a
        // read hiccuped is the exact defect this closes.
        fetchServerProfile()
          .then((profile) => {
            if (!profile) return;
            setUserAccount((prev) => (prev ? { ...prev, ...profile } as UserAccount : prev));
          })
          .catch(() => { /* fetchServerProfile never rejects; belt and braces */ });

        // Track login and update trial status
        try {
          await UsageTrackingService.trackLogin(firebaseUser.uid);
        } catch (_error) {
          // Login tracking is non-critical
        }

        // Set user ID + enriched properties for analytics. Properties ride
        // along on every subsequent event for segmentation in GA4 / BigQuery.
        analyticsService.setUserId(firebaseUser.uid);
        // Our own accounts, tagged so a GA4 filter can drop them from the
        // funnel (ROADMAP 0.R R12). Loaded on demand: the allow-list lives with
        // the admin gate, which itself imports this module.
        void import('../pages/IndexAdmin/AdminGate')
          .then(({ isAdminEmail }) => analyticsService.setUserProperties({
            internal_user: isAdminEmail(firebaseUser.email) ? 'true' : 'false',
          }))
          .catch(() => { /* a tag is never worth a failed sign-in */ });
        const firstTouch = getFirstTouch();
        const anonymousId = getAnonymousId();
        const creationTime = firebaseUser.metadata.creationTime
          ? new Date(firebaseUser.metadata.creationTime)
          : null;
        const lastSignInTime = firebaseUser.metadata.lastSignInTime
          ? new Date(firebaseUser.metadata.lastSignInTime)
          : null;
        const daysSinceSignup = creationTime
          ? Math.floor((Date.now() - creationTime.getTime()) / 86400000)
          : null;
        // Detect first sign-in: createdAt and lastSignInAt are within ~5s of
        // each other on a brand-new account.
        const isFirstSignIn = !!(creationTime && lastSignInTime &&
          Math.abs(lastSignInTime.getTime() - creationTime.getTime()) < 5000);

        analyticsService.setUserProperties({
          email_domain: firebaseUser.email?.split('@')[1] || 'unknown',
          provider: firebaseUser.providerData[0]?.providerId || 'unknown',
          // subscription_tier is set from the real plan on the user-doc
          // snapshot below (setUserProperties merges), not asserted here.
          is_authenticated: true,
          signup_date: creationTime ? creationTime.toISOString().slice(0, 10) : null,
          days_since_signup: daysSinceSignup,
          signup_cohort_week: creationTime ? isoWeek(creationTime) : null,
          anonymous_id: anonymousId, // for pre/post-signin stitching in BigQuery
          utm_source: firstTouch.utm_source,
          utm_medium: firstTouch.utm_medium,
          utm_campaign: firstTouch.utm_campaign,
          initial_referrer: firstTouch.initial_referrer,
          is_mobile: typeof window !== 'undefined' && window.innerWidth < 768,
          browser_locale: typeof navigator !== 'undefined' ? navigator.language : 'unknown',
        });

        // Emit user_identified once per auth state change so BigQuery can
        // join pre-signup events (anonymous_id only) with post-signup events
        // (anonymous_id + user_id). Critical for "time to first signup"
        // and "what did they do before signing up" queries.
        analyticsService.logEvent('user_identified', {
          prior_anonymous_id: anonymousId,
          provider: firebaseUser.providerData[0]?.providerId || 'unknown',
          is_first_signin: isFirstSignIn,
          utm_source: firstTouch.utm_source,
          utm_campaign: firstTouch.utm_campaign,
        });

        // Check for "Explore First" deferred conversion
        try {
          const exploreSession = localStorage.getItem('explore_first_session');
          if (exploreSession) {
            const { timestamp } = JSON.parse(exploreSession);
            const hoursElapsed = (Date.now() - timestamp) / (1000 * 60 * 60);

            analyticsService.logEvent('explore_first_converted', {
              hoursToConvert: Math.round(hoursElapsed),
              daysToConvert: Math.round(hoursElapsed / 24),
            });

            localStorage.removeItem('explore_first_session');
          }
        } catch {
          // Ignore localStorage errors
        }
        
        // Set up real-time listener for updates
        const userDocRef = doc(db, 'users', firebaseUser.uid);
        unsubscribeSnapshot = onSnapshot(
          userDocRef,
          (snapshot) => {
            if (!snapshot.exists()) {
              // No user doc yet (a brand-new sign-up, before provisioning
              // writes one) — that is a RESOLVED answer, not an unknown one:
              // no doc means no plan and no grant. Marking it resolved lets a
              // paid upsell render for a genuinely free user, while a failed
              // read below still leaves it unresolved ([[BUGS #147]]).
              useSubscriptionStore.getState().setPlan('free');
            }
            if (snapshot.exists()) {
              const data = snapshot.data();

              // Convert onboardingSurvey timestamps if present
              const onboardingSurvey = data.onboardingSurvey ? {
                ...data.onboardingSurvey,
                completedAt: data.onboardingSurvey.completedAt?.toDate ? data.onboardingSurvey.completedAt.toDate() : data.onboardingSurvey.completedAt
              } : undefined;

              setUserAccount({
                uid: snapshot.id,
                ...data,
                createdAt: data.createdAt?.toDate() || new Date(),
                updatedAt: data.updatedAt?.toDate() || new Date(),
                avatarGeneratedAt: data.avatarGeneratedAt?.toDate(),
                onboardingSurvey: onboardingSurvey,
                // Server-owned fields keep what the GATEWAY last told us
                // ([[BUGS #190]]). This snapshot reads the PRODUCTION project —
                // Auth is shared, so the web SDK is pinned there — while
                // `update-profile` writes the project the backend runs in. On
                // staging those are different databases, which is what reverted
                // a saved theme and undid a dismissal on every load.
                ...getServerProfile(),
              } as UserAccount);

              // Entitlements are NOT read here ([[BUGS #153]]). They come from
              // `service=user&action=entitlements` — the server that enforces
              // them — because reconciling `plan` with `grantedPlan` in the
              // client was a second implementation of a decision the server
              // already owns, over a database that is not always the one the
              // server writes to ([[BUGS #152]]). Mirroring both would be
              // worse than either: this snapshot would race the fetch and, on
              // staging, overwrite the right answer with the wrong one.
              analyticsService.setUserProperties({
                subscription_tier: useSubscriptionStore.getState().plan,
              });
            }
          },
          (error) => {
            // Deliberately NOT resolving entitlements here. A read that failed
            // tells us nothing about what this user pays for, and the store's
            // initial `free` would otherwise be indistinguishable from a
            // measured one — which is how a subscriber gets shown a paywall
            // ([[BUGS #147]]). Unresolved keeps paid upsells silent; the
            // server still gates every paid action.
            // Provide more specific error messages
            if (error.code === 'permission-denied') {
              setError('Permission denied. Please ensure you are signed in with the correct account.');
            } else if (error.code === 'unavailable') {
              setError('Firestore service is temporarily unavailable. Please try again later.');
            } else {
              setError(`Failed to sync user data: ${error.message}`);
            }
          }
        );
        
        setLoading(false);
      } else {
        // No user signed in
        setUserAccount(null);
        clearServerProfile();
        useSubscriptionStore.getState().setPlan('free');
        setError(null);
        setLoading(false);
      }
    });

    // Cleanup
    return () => {
      unsubscribeAuth();
      if (unsubscribeSnapshot) {
        unsubscribeSnapshot();
      }
    };
  }, [loadUserAccount]);

  // Auto-save pending portfolio after authentication
  // This handles the case where user started saving a portfolio, was redirected to sign in,
  // and needs their portfolio saved automatically after successful authentication
  const { pendingPortfolio, clearPendingPortfolio } = usePendingSaveStore();
  const { savePortfolio } = useSavedPortfolioStore();

  useEffect(() => {
    const autoSavePendingPortfolio = async () => {
      // Only proceed if user is authenticated and there's a pending portfolio
      if (!user?.uid || !pendingPortfolio || loading) return;

      try {
        // Generate portfolio name: "Portfolio - Jan 10, 2026"
        const now = new Date();
        const options: Intl.DateTimeFormatOptions = {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        };
        const portfolioName = `Portfolio - ${now.toLocaleDateString('en-US', options)}`;

        // Save the portfolio
        const portfolioId = await savePortfolio(user.uid, portfolioName, pendingPortfolio.holdings);

        if (portfolioId) {
          clearPendingPortfolio();
          // Note: Success notification is shown via the SavePortfolioModal's onSaveSuccess callback
          // when user manually triggers save. For auto-save, the user will see their portfolio
          // in "My Portfolios" page. We could add a global snackbar here if needed.
        }
      } catch (_error) {
        // Don't clear the pending portfolio on error - user can try again
      }
    };

    autoSavePendingPortfolio();
  }, [user?.uid, pendingPortfolio, loading, savePortfolio, clearPendingPortfolio]);

  const signInWithGoogle = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // A touch phone at the auth domain's own origin redirects: in-app mail
      // browsers block popups, and a redirect survives them (ROADMAP 1.37).
      const env = currentSignInEnvironment(auth.config.authDomain);
      if (googleSignInMode(env) === 'redirect') {
        markRedirectPending();
        await signInWithRedirect(auth, googleProvider, browserPopupRedirectResolver);
        return 'redirecting'; // the page navigates away; the boot handler finishes the job
      }

      let result;
      try {
        result = await signInWithPopup(auth, googleProvider, browserPopupRedirectResolver);
      } catch (popupError: any) {
        if (shouldFallBackToRedirect(popupError?.code, env)) {
          markRedirectPending();
          await signInWithRedirect(auth, googleProvider, browserPopupRedirectResolver);
          return 'redirecting';
        }
        throw popupError;
      }

      // (removed 2026-06-07) propagateAuthToBackend used to POST to
      // /api/auth/verify here — the endpoint never existed and the call
      // always CORS-failed silently. Backend user docs are now created
      // lazily on first profile update via api_gateway?service=user.

      // Track analytics
      trackEvents.login('google');

      const additionalUserInfo = getAdditionalUserInfo(result);
      if (additionalUserInfo?.isNewUser) {
        trackEvents.signUp('google');
        // Track new sign-up with our analytics
        analyticsService.trackSignUp('google', 'login_dialog', result.user.email || undefined);
      }

      // Remembered so a later visit may offer automatic sign-in (ROADMAP 1.37 item 4).
      try { localStorage.setItem('ia:signedInWithGoogle', '1'); } catch { /* storage blocked */ }
      // User account will be loaded by onAuthStateChanged listener
      // Reset loading here — onAuthStateChanged will manage its own loading state
      setLoading(false);
      return 'signed-in';
    } catch (error: any) {
      // Handle MFA required - show TOTP verification dialog
      if (error.code === 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(auth, error);
        setMfaResolver(resolver);
        setMfaPending(true);
        setLoading(false);
        return 'incomplete';
      }

      // An account already uses this email with another sign-in method
      // ([[BUGS #429]]). Never say which: with email-enumeration protection
      // on, Firebase hides it, and naming it would tell a stranger how an
      // address signs in.
      if (error.code === 'auth/account-exists-with-different-credential') {
        setError(ACCOUNT_EXISTS_MESSAGE);
        trackEvents.errorOccurred('auth_account_exists', 'Google', 'signInWithGoogle');
        setLoading(false);
        return 'incomplete';
      }
      
      let errorMessage = 'Failed to sign in';
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Sign in cancelled';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Please allow popups for this site';
      }
      
      setError(errorMessage);
      setLoading(false);
      trackEvents.errorOccurred('auth_error', error.message || errorMessage, 'signInWithGoogle');
      throw error; // Re-throw to let caller handle it
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackEvents]);

  const signInWithGoogleCredential = useCallback(async (idToken: string): Promise<GoogleSignInOutcome> => {
    try {
      setError(null);
      const result = await signInWithCredential(auth, GoogleAuthProvider.credential(idToken));
      trackEvents.login('google');
      analyticsService.trackSignIn('success', 'google', 'one_tap');
      const info = getAdditionalUserInfo(result);
      if (info?.isNewUser) {
        trackEvents.signUp('google');
        // No email: One Tap sign-ups are logged without the address (invariant 10).
        analyticsService.trackSignUp('google', 'one_tap');
      }
      try { localStorage.setItem('ia:signedInWithGoogle', '1'); } catch { /* storage blocked */ }
      return 'signed-in';
    } catch (error: any) {
      if (error?.code === 'auth/multi-factor-auth-required') {
        setMfaResolver(getMultiFactorResolver(auth, error));
        setMfaPending(true);
        return 'incomplete';
      }
      console.warn('[auth] One Tap sign-in did not complete:', error?.code || 'unknown');
      trackEvents.errorOccurred('auth_error', error?.code || 'one_tap_failed', 'signInWithGoogleCredential');
      return 'incomplete';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackEvents]);

  const signInWithMicrosoft = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      try {
        // Use Firebase's standard Microsoft OAuth flow
        const result = await MicrosoftAuthStandard.signInWithPopup();

        // (removed 2026-06-07 — see google flow above for context)

        // A blocked popup falls back to a redirect and returns nothing: the
        // page is leaving, and the redirect handler records the sign-in when
        // it comes back — counting it here too would count it twice
        // ([[BUGS #527]]).
        if (!result) return;

        // Track analytics
        trackEvents.login('microsoft');

        const additionalUserInfo = getAdditionalUserInfo(result);
        if (additionalUserInfo?.isNewUser) {
          trackEvents.signUp('microsoft');
          // Track new sign-up with our analytics
          analyticsService.trackSignUp('microsoft', 'login_dialog', result.user.email || undefined);
        }

        // User account will be loaded by onAuthStateChanged listener
        setLoading(false);
      } catch (error: any) {
        // Re-throw to be handled by outer catch
        throw error;
      }
    } catch (error: any) {
      // Handle MFA required - show TOTP verification dialog
      if (error.code === 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(auth, error);
        setMfaResolver(resolver);
        setMfaPending(true);
        setLoading(false);
        return;
      }

      // An account already uses this email with another sign-in method
      // ([[BUGS #429]]). Never say which: with email-enumeration protection
      // on, Firebase hides it, and naming it would tell a stranger how an
      // address signs in.
      if (error.code === 'auth/account-exists-with-different-credential') {
        setError(ACCOUNT_EXISTS_MESSAGE);
        trackEvents.errorOccurred('auth_account_exists', 'Microsoft', 'signInWithMicrosoft');
        setLoading(false);
        return;
      }

      let errorMessage = 'Failed to sign in with Microsoft';
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Sign in cancelled';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your connection';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Popup blocked. Please allow popups for this site';
      } else if (error.code === 'auth/invalid-credential' || error.message?.includes('server_error')) {
        errorMessage = 'Microsoft login configuration error. Please use Google to sign in or contact support.';
      }
      
      setError(errorMessage);
      setLoading(false);
      trackEvents.errorOccurred('auth_error', error.message || errorMessage, 'signInWithMicrosoft');
      throw error; // Re-throw to let caller handle it
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackEvents]);

  const signInWithTwitter = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await signInWithPopup(auth, twitterProvider, browserPopupRedirectResolver);

      // (removed 2026-06-07 — see google flow above for context)

      // Track analytics
      trackEvents.login('twitter');
      
      // Check if this is a new user
      const isNewUser = result.user.metadata.creationTime === result.user.metadata.lastSignInTime;
      if (isNewUser) {
        trackEvents.signUp('twitter');
        analyticsService.trackSignUp('twitter', 'login_dialog', result.user.email || undefined);
      }

      // User account will be loaded by onAuthStateChanged listener
      setLoading(false);
    } catch (error: any) {
      // Handle MFA required - show TOTP verification dialog
      if (error.code === 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(auth, error);
        setMfaResolver(resolver);
        setMfaPending(true);
        setLoading(false);
        return;
      }

      // An account already uses this email with another sign-in method
      // ([[BUGS #429]]). Never say which: with email-enumeration protection
      // on, Firebase hides it, and naming it would tell a stranger how an
      // address signs in.
      if (error.code === 'auth/account-exists-with-different-credential') {
        setError(ACCOUNT_EXISTS_MESSAGE);
        trackEvents.errorOccurred('auth_account_exists', 'X', 'signInWithTwitter');
        setLoading(false);
        return;
      }

      let errorMessage = 'Failed to sign in with X';
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Sign in cancelled';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your connection';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Popup blocked. Please allow popups for this site';
      } else if (error.code === 'auth/invalid-credential') {
        errorMessage = 'X login is not configured yet. Please use Google to sign in.';
      }
      
      setError(errorMessage);
      setLoading(false);
      trackEvents.errorOccurred('auth_error', error.message || errorMessage, 'signInWithTwitter');
      throw error; // Re-throw to let caller handle it
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackEvents]);

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);

      await signInWithEmailAndPassword(auth, email, password);

      // Track analytics
      trackEvents.login('email');
      setLoading(false);
    } catch (error: any) {
      // Handle MFA required - show TOTP verification dialog
      if (error.code === 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(auth, error);
        setMfaResolver(resolver);
        setMfaPending(true);
        setLoading(false);
        return;
      }

      let errorMessage = 'Failed to sign in';
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.code === 'auth/user-disabled') {
        errorMessage = 'This account has been disabled';
      } else if (error.code === 'auth/invalid-credential') {
        errorMessage = 'Invalid email or password';
      }

      setError(errorMessage);
      setLoading(false);
      trackEvents.errorOccurred('auth_error', error.message || errorMessage, 'signInWithEmail');
      throw error;
    }
  }, [trackEvents]);

  const signUpWithEmail = useCallback(async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await createUserWithEmailAndPassword(auth, email, password);

      // Track analytics
      trackEvents.signUp('email');
      analyticsService.trackSignUp('email', 'login_dialog', result.user.email || undefined);
      setLoading(false);
    } catch (error: any) {
      let errorMessage = 'Failed to create account';
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'An account already exists with this email';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password should be at least 6 characters';
      }
      
      setError(errorMessage);
      setLoading(false);
      trackEvents.errorOccurred('auth_error', error.message || errorMessage, 'signUpWithEmail');
      throw error; // Re-throw to let caller handle it
    }
  }, [trackEvents]);

  const signOut = useCallback(async () => {
    try {
      await firebaseSignOut(auth);
      trackEvents.logout();
      setError(null);

      // Reset all Zustand stores to prevent data leaking between user sessions
      try {
        const { useChatStore } = await import('../stores/chatStore');
        const { useRiskReportStore } = await import('../stores/riskReportStore');
        const { useLevelUpStore } = await import('../stores/levelUpStore');
        const { useCreditStore } = await import('../stores/creditStore');
        const { useNotificationStore } = await import('../stores/notificationStore');
        const { useSearchHistoryStore } = await import('../stores/searchHistoryStore');
        const { useActiveChatStore } = await import('../stores/activeChatStore');

        useChatStore.getState().switchSession('');  // Clear current session
        useChatStore.setState({ sessions: [], currentSessionId: null, messageQueue: [], sessionTokenUsage: {}, sessionSummaries: {} });
        useRiskReportStore.getState().clearSession();
        useLevelUpStore.getState().reset();
        useCreditStore.getState().reset();
        useNotificationStore.getState().reset();
        useSearchHistoryStore.getState().clearSession();
        useSubscriptionStore.getState().setPlan('free');  // drop tier on sign-out
        useActiveChatStore.getState().resetChat('');
        // The rung, and the fact that somebody chose it ([[BUGS #298]]).
        // `resetChat` deliberately leaves these alone — it also runs when a
        // reader starts a NEW chat, and wiping their pick there would be its
        // own defect. On sign-out they must go, or the next reader in this tab
        // inherits a choice that was not theirs and the entitlement-aware
        // default stays silent for them. Caught by review.
        useActiveChatStore.setState({ selectedTier: 'smart', tierChosenByUser: false });

        // Reset additional stores to prevent data leaking between user sessions
        const { useSavedPortfolioStore } = await import('../stores/savedPortfolioStore');
        const { useWatchlistStore } = await import('../stores/watchlistStore');
        const { usePendingSaveStore } = await import('../stores/pendingSaveStore');
        const { useGuestOnboardingStore } = await import('../stores/guestOnboardingStore');

        useSavedPortfolioStore.setState({ portfolios: [], isLoading: false, error: null });
        useWatchlistStore.setState({ watchlists: [], isLoading: false, error: null });
        usePendingSaveStore.getState().clearPendingPortfolio();
        useGuestOnboardingStore.getState().clearGuestTasks();

        // Clear localStorage for persisted stores
        localStorage.removeItem('indexalpha-chat-sessions');
        localStorage.removeItem('indexalpha-pending-portfolio-save');
        localStorage.removeItem('indexalpha-guest-onboarding');
      } catch {
        // Store cleanup is best-effort; auth signout already succeeded
      }
    } catch (error: any) {
      setError('Failed to sign out');
      trackEvents.errorOccurred('auth_error', 'Sign out failed', 'signOut');
    }
  }, [trackEvents]);

  const value: AuthContextType = useMemo(() => ({
    user,
    userAccount,
    loading,
    error,
    mfaResolver,
    mfaPending,
    signInWithGoogle,
    signInWithGoogleCredential,
    signInWithMicrosoft,
    signInWithTwitter,
    signInWithEmail,
    signUpWithEmail,
    signOut,
    refreshUserAccount,
    clearMfaState,
    onMfaSuccess
  }), [
    user,
    userAccount,
    loading,
    error,
    mfaResolver,
    mfaPending,
    signInWithGoogle,
    signInWithGoogleCredential,
    signInWithMicrosoft,
    signInWithTwitter,
    signInWithEmail,
    signUpWithEmail,
    signOut,
    refreshUserAccount,
    clearMfaState,
    onMfaSuccess
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};